import { useEffect, useState } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { Download, IndianRupee, Trophy, Users, Target, FileSpreadsheet } from "lucide-react";
import { DateRangePicker, presetRange } from "@/components/DateRangePicker";

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3 hard-shadow-sm">
      <div className="h-10 w-10 rounded-lg bg-klein/10 text-klein grid place-items-center"><Icon className="h-5 w-5" /></div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="font-head text-xl font-black">{value}</div>
      </div>
    </div>
  );
}

function Table({ title, rows, cols, tid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden hard-shadow-sm" data-testid={tid}>
      <div className="px-4 py-3 border-b border-zinc-200 font-head font-bold text-sm">{title}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50/60">
            {cols.map((c) => <th key={c.k} className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60">
              {cols.map((c) => <td key={c.k} className={`px-4 py-2.5 ${c.right ? "text-right font-mono tabular-nums" : ""}`}>{c.fmt ? c.fmt(r[c.k]) : r[c.k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Reports() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [range, setRange] = useState({ key: "all", ...presetRange("all") });
  useEffect(() => {
    const params = {};
    if (range?.start) params.start = range.start;
    if (range?.end) params.end = range.end;
    api.get("/reports/summary", { params }).then((r) => setD(r.data)).catch((e) => setErr(errText(e)));
  }, [range]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/reports/leads.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "sales-iq-leads.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV");
    } catch { toast.error("Export failed"); }
  };

  if (err) return <div className="p-6 text-sm text-red-600 font-mono" data-testid="report-error">Could not load report: {err}</div>;
  if (!d) return <div className="p-6 font-mono text-sm">Loading report…</div>;
  const t = d.totals;
  const money = (v) => `₹${(v || 0).toLocaleString()}`;
  const pct = (v) => `${v || 0}%`;

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight">Sales Report</h1>
          <p className="text-xs text-zinc-500 font-mono">Generated {new Date(d.generated_at).toLocaleString()} · {range.label}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangePicker value={range} onChange={setRange} />
          <button data-testid="export-csv" onClick={exportCsv}
            className="flex items-center gap-2 bg-klein text-white rounded-lg px-4 py-2 text-sm font-bold hover:bg-klein-hover transition-colors hard-shadow-sm">
            <FileSpreadsheet className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-3 mb-5">
        <KPI icon={Users} label="Total leads" value={t.leads} />
        <KPI icon={Trophy} label="Won" value={t.won} />
        <KPI icon={Target} label="Win rate" value={`${t.win_rate}%`} />
        <KPI icon={IndianRupee} label="Revenue" value={money(t.revenue)} />
        <KPI icon={IndianRupee} label="Avg deal" value={money(t.avg_deal)} />
      </div>

      <div className="space-y-5">
        <Table title="Rep performance" tid="report-reps" rows={d.by_rep} cols={[
          { k: "name", label: "Rep" },
          { k: "total", label: "Leads", right: true },
          { k: "active", label: "Active", right: true },
          { k: "won", label: "Won", right: true },
          { k: "lost", label: "Lost", right: true },
          { k: "win_rate", label: "Win %", right: true, fmt: pct },
          { k: "revenue", label: "Revenue", right: true, fmt: money },
        ]} />

        <div className="grid lg:grid-cols-2 gap-5">
          <Table title="By tier" tid="report-tier" rows={d.by_tier} cols={[
            { k: "label", label: "Tier" },
            { k: "total", label: "Leads", right: true },
            { k: "won", label: "Won", right: true },
            { k: "win_rate", label: "Win %", right: true, fmt: pct },
            { k: "revenue", label: "Revenue", right: true, fmt: money },
          ]} />
          <Table title="By source" tid="report-source" rows={d.by_source} cols={[
            { k: "label", label: "Source" },
            { k: "total", label: "Leads", right: true },
            { k: "won", label: "Won", right: true },
            { k: "win_rate", label: "Win %", right: true, fmt: pct },
            { k: "revenue", label: "Revenue", right: true, fmt: money },
          ]} />
        </div>
      </div>
    </div>
  );
}
